import React, { useState } from 'react';
import { Box, Paper, Typography, Button, TextField, Chip } from '@mui/material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import api from '../../api';

const ApprovalsTab = ({ empId, activeMenu, pendingLeaves, historyLeaves, showMsg, refreshData }) => {
  const [remarks, setRemarks] = useState({});

  const handleApproval = async (reqId, action) => {
    try {
      await api.put(`/leave/approve/${reqId}?manager_id=${empId}&action=${action}&remarks=${remarks[reqId] || ''}`);
      showMsg(`Request ${action}D successfully!`);
      refreshData();
    } catch (err) { showMsg("Failed to process request.", "error"); }
  };

  const renderStackedCell = (primary, secondary) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
      <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>{primary || '--'}</Typography>
      <Typography variant="caption" color="text.secondary">{secondary || '--'}</Typography>
    </Box>
  );

  const sharedCols = [
    { field: 'emp_details', headerName: 'Employee & ID', width: 220, disableColumnMenu: false, valueGetter: (p, r) => `${r.employee_name} ${r.emp_id}`, renderCell: (p) => renderStackedCell(p.row.employee_name, p.row.emp_id) },
    { field: 'dept_details', headerName: 'Dept & Designation', width: 220, disableColumnMenu: true, valueGetter: (p, r) => `${r.department} ${r.designation}`, renderCell: (p) => renderStackedCell(p.row.department, p.row.designation) },
    { field: 'leave_type', headerName: 'Type', width: 90, disableColumnMenu: true, renderCell: (p) => <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{p.row.leave_type}</Typography> },
    { field: 'dates', headerName: 'Dates', width: 180, disableColumnMenu: true, valueGetter: (p, r) => `${r.start_date} to ${r.end_date}` },
    { field: 'reason', headerName: 'Reason', flex: 1, minWidth: 150, disableColumnMenu: true }
  ];

  const pendingCols = [
    ...sharedCols,
    { field: 'remarks', headerName: 'Remarks', width: 180, sortable: false, disableColumnMenu: true, renderCell: (p) => (
      <TextField size="small" placeholder="Add remark..." fullWidth onChange={e => setRemarks({...remarks, [p.row.id]: e.target.value})} sx={{ mt: 1 }} />
    )},
    { field: 'action', headerName: 'Action', width: 180, sortable: false, disableColumnMenu: false, renderCell: (p) => (
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', height: '100%' }}>
        <Button size="small" variant="contained" color="success" disableElevation onClick={() => handleApproval(p.row.id, 'APPROVE')}>Approve</Button>
        <Button size="small" variant="contained" color="error" disableElevation onClick={() => handleApproval(p.row.id, 'REJECT')}>Reject</Button>
      </Box>
    )}
  ];

  const historyCols = [
    ...sharedCols,
    { field: 'manager_remarks', headerName: 'Remarks', width: 180, disableColumnMenu: true, valueGetter: (p, r) => r.l1_approver === empId ? r.l1_remarks : r.l2_remarks },
    { field: 'action_date', headerName: 'Action Date', width: 120, disableColumnMenu: true, renderCell: (p) => (
      <Typography variant="body2">{p.row.updated_at ? new Date(p.row.updated_at).toLocaleDateString() : '--'}</Typography>
    )},
    { field: 'status', headerName: 'Status', width: 110, disableColumnMenu: false, renderCell: (p) => (
      <Chip size="small" label={p.row.status} color={p.row.status === 'APPROVED' ? 'success' : 'error'} sx={{ fontWeight: 'bold', fontSize: '0.7rem' }} />
    )}
  ];

  const isPending = activeMenu === 'app_pending';

  return (
    <Paper elevation={2} sx={{ borderRadius: 2, height: '100%', display: 'flex', flexDirection: 'column', textAlign: 'left' }}>
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', bgcolor: '#fbfbfb' }}>
        <Typography variant="h6" color={isPending ? "warning.main" : "textSecondary"} sx={{ fontWeight: 'bold' }}>
          {isPending ? "Pending Approvals" : "Approval History"}
        </Typography>
      </Box>
      <Box sx={{ flexGrow: 1, p: 2, height: 600 }}>
        <DataGrid
          rows={isPending ? pendingLeaves : historyLeaves}
          columns={isPending ? pendingCols : historyCols}
          slots={{ toolbar: GridToolbar }}
          slotProps={{ toolbar: { showQuickFilter: true, quickFilterProps: { debounceMs: 500 } } }}
          initialState={{ pagination: { paginationModel: { pageSize: 15 } } }}
          pageSizeOptions={[10, 15, 30, 50]}
          disableRowSelectionOnClick
          sx={{
            border: 'none',
            '& .MuiDataGrid-columnHeaders': { bgcolor: '#f8fafc', fontWeight: 'bold' },
            '& .MuiDataGrid-columnHeader:first-of-type, & .MuiDataGrid-cell:first-of-type': {
              position: 'sticky', left: 0, zIndex: 3, backgroundColor: '#fff', boxShadow: '2px 0 4px -2px rgba(0,0,0,0.2)'
            }
          }}
        />
      </Box>
    </Paper>
  );
};

export default ApprovalsTab;